import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import type { FormativeHomeCard } from '../../tutor/api/malayFormativeProgress.client';
import type { TutorAnalyticsSummary } from '../../../tutor/types/tutorAnalytics';
import { buildMalayTodaysLearningSnapshot } from '../utils/malayTodaysLearning';
import { DashboardCardSkeleton, DashboardEmptyState } from './dashboardUi';

type Props = {
  formativeCards: FormativeHomeCard[];
  tutorAnalytics: TutorAnalyticsSummary | null;
  loading: boolean;
  enabled: boolean;
};

export function MalayTodaysLearningCard({
  formativeCards,
  tutorAnalytics,
  loading,
  enabled,
}: Props) {
  const { t } = useTranslation();

  const snapshot = useMemo(
    () =>
      buildMalayTodaysLearningSnapshot({
        formativeCards,
        tutorAnalytics,
      }),
    [formativeCards, tutorAnalytics],
  );

  if (!enabled) return null;

  if (loading) {
    return <DashboardCardSkeleton className="mb-6" />;
  }

  return (
    <section className="mb-6" aria-labelledby="malay-todays-learning-heading">
      <Card className="border-purple-200/60 dark:border-purple-500/25">
        <CardHeader className="pb-2">
          <CardTitle
            id="malay-todays-learning-heading"
            className="flex items-center gap-2 text-base text-slate-900 dark:text-white"
          >
            <CalendarDays className="h-5 w-5 text-purple-600 dark:text-purple-300" aria-hidden />
            {t('dashboard.studentHome.todaysLearning')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!snapshot.hasAnyData ? (
            <DashboardEmptyState>
              {t('dashboard.studentHome.malayTodaysLearning.empty')}
            </DashboardEmptyState>
          ) : (
            <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
              {snapshot.tutorMinutesToday != null ? (
                <li data-testid="malay-today-tutor-minutes">
                  {t('dashboard.studentHome.malayTodaysLearning.tutorMinutes', {
                    minutes: snapshot.tutorMinutesToday,
                  })}
                </li>
              ) : null}
              {snapshot.tutorSessionsToday != null ? (
                <li data-testid="malay-today-tutor-sessions">
                  {t('dashboard.studentHome.malayTodaysLearning.tutorSessions', {
                    count: snapshot.tutorSessionsToday,
                  })}
                </li>
              ) : null}
              {snapshot.activitiesInProgress > 0 ? (
                <li data-testid="malay-today-in-progress">
                  {t('dashboard.studentHome.malayTodaysLearning.inProgress', {
                    count: snapshot.activitiesInProgress,
                  })}
                </li>
              ) : null}
              {snapshot.activitiesCompleted > 0 ? (
                <li data-testid="malay-today-completed">
                  {t('dashboard.studentHome.malayTodaysLearning.completed', {
                    count: snapshot.activitiesCompleted,
                  })}
                </li>
              ) : null}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
